/**
 * PillSelect: a list of choices opened from a pill (Figma 00): the category switch in the bar ("Definitions
 * 119 of 223 ▾") and the "change kind" of a box or leaf. It is `Choice` with its pill look: a trigger the caller
 * words, the choice reported by key. `KindPick` is the kind's chip and its change; `CategorySwitch` is the bar's
 * trigger with its name, its count and the caret.
 */
import type { ReactNode } from 'react';
import type { Key } from 'react-aria-components';
import { SPACE, TEXT, WEIGHT, usePalette } from '../theme';
import { Chip, INK } from './chip';
import { Choice, type PillShape } from './choice';

export type PillChoice<K extends Key> = { key: K; label: string };

export function PillSelect<K extends Key>({
  name,
  listLabel,
  options,
  value,
  onChange,
  look,
  children,
}: {
  /** The trigger's accessible name: what it picks and what it says now, so it contains the words it shows (label in name). */
  name: string;
  /** The open list's name. */
  listLabel: string;
  options: readonly PillChoice<K>[];
  /** The chosen key, or null while nothing is chosen (the trigger then says so). */
  value: K | null;
  onChange: (key: K) => void;
  look: PillShape;
  /** What the trigger says. */
  children: ReactNode;
}) {
  return (
    <Choice
      label={listLabel}
      options={options}
      look={{ kind: 'pill', shape: look, name, says: children }}
      selectedKey={value}
      onSelectionChange={(key) => {
        const chosen = options.find((option) => option.key === key);
        if (chosen !== undefined) onChange(chosen.key);
      }}
    />
  );
}

/** A box's kind: the dark chip that names it and the outlined "change" that opens the kinds to choose from. */
export function KindPick<K extends Key>({
  lead,
  options,
  value,
  changing,
}: {
  /** A word before the chip ("kind"), or none. */
  lead?: string;
  options: readonly PillChoice<K>[];
  value: K;
  /** Whether the kind can be changed from here: the change control's words and what it reports, or the chip alone. */
  changing:
    | { readonly kind: 'none' }
    | {
        readonly kind: 'to';
        readonly label: string;
        readonly onChange: (key: K) => void;
      };
}) {
  const p = usePalette();
  const named = options.find((option) => option.key === value);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.tight }}>
      {lead !== undefined && <span style={{ fontSize: TEXT.micro, color: p.inkMuted }}>{lead}</span>}
      <Chip look={INK} size="row">
        {named?.label}
      </Chip>
      {changing.kind === 'to' && (
        <PillSelect
          name={`Change kind (now ${named?.label ?? value})`}
          listLabel="Kind"
          options={options}
          value={value}
          onChange={changing.onChange}
          look="outline"
        >
          {changing.label}
        </PillSelect>
      )}
    </div>
  );
}

/** The bar's category switch: the category's name, where the walk stands in it, and the caret. */
export function CategorySwitch<K extends Key>({
  label,
  count,
  options,
  value,
  onChange,
}: {
  /** The category's name. */
  label: string;
  /** Where the walk stands in the category, or what it holds; none when the contract has no number to give. */
  count?: string;
  options: readonly PillChoice<K>[];
  value: K;
  onChange: (key: K) => void;
}) {
  const p = usePalette();
  return (
    <PillSelect name={`Category: ${label}${count === undefined ? '' : ` ${count}`}`} listLabel="Category" options={options} value={value} onChange={onChange} look="raised">
      <span style={{ fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink }}>{label}</span>
      {count !== undefined && <span style={{ fontSize: TEXT.control, color: p.inkMuted }}>{count}</span>}
      <span aria-hidden="true" style={{ fontSize: TEXT.caption, color: p.inkMuted }}>
        ▾
      </span>
    </PillSelect>
  );
}

/** Which kind a box has been given: one the reviewer picked, or none yet (never a default). */
export type KindChosen<K extends Key> = { readonly kind: 'chosen'; readonly key: K } | { readonly kind: 'unchosen' };

/** The kind a box must be told: the outlined pill that reads "choose a kind" until one is picked, then names it. */
export function KindChoose<K extends Key>({ options, chosen, onChange }: { options: readonly PillChoice<K>[]; chosen: KindChosen<K>; onChange: (key: K) => void }) {
  const p = usePalette();
  const named = chosen.kind === 'chosen' ? options.find((option) => option.key === chosen.key) : undefined;
  return (
    <PillSelect
      name={`Kind: ${named?.label ?? 'choose a kind'}`}
      listLabel="Kind"
      options={options}
      value={chosen.kind === 'chosen' ? chosen.key : null}
      onChange={onChange}
      look="outline"
    >
      <span style={{ color: named === undefined ? p.inkMuted : p.ink }}>{named?.label ?? 'choose a kind'}</span>
      <span aria-hidden="true" style={{ fontSize: TEXT.micro, color: p.inkMuted }}>
        ▾
      </span>
    </PillSelect>
  );
}
